import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { ChatCircleDots, PaperPlaneTilt, X, Headset, ArrowLeft, CheckCircle } from "@phosphor-icons/react";

// Matches markdown links [label](url), **bold**, bare http(s) URLs, and bare /internal-paths.
const TOKEN_RE = /(\[[^\]]+\]\((?:\/[^\s)]+|https?:\/\/[^\s)]+)\))|(\*\*[^*]+\*\*)|(https?:\/\/[^\s]+)|(\/[a-zA-Z0-9][a-zA-Z0-9\-_/?=&]*)/g;

// Renders assistant text with clickable in-app links (internal paths navigate via
// react-router and close the widget; external URLs open in a new tab) plus **bold**.
function RichText({ text, onNavigate }) {
  const linkCls = "text-[#FF7A1A] font-semibold underline decoration-[#FF7A1A]/60 underline-offset-2 hover:decoration-[#FF7A1A]";
  const makeLink = (url, label, key) => {
    if (/^https?:/i.test(url)) {
      return <a key={key} href={url} target="_blank" rel="noopener noreferrer" className={linkCls}>{label}</a>;
    }
    return <Link key={key} to={url} onClick={onNavigate} className={linkCls} data-testid="assistant-link">{label}</Link>;
  };
  const nodes = [];
  let last = 0, m, key = 0;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text || "")) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (m[1]) {
      const mm = /^\[([^\]]+)\]\((.+)\)$/.exec(tok);
      nodes.push(makeLink(mm[2], mm[1], key++));
    } else if (m[2]) {
      nodes.push(<strong key={key++} className="text-white font-bold">{tok.slice(2, -2)}</strong>);
    } else if (m[3]) {
      nodes.push(makeLink(tok, tok, key++));
    } else if (m[4]) {
      nodes.push(makeLink(tok, tok, key++));
    }
    last = m.index + tok.length;
  }
  if (last < (text || "").length) nodes.push(text.slice(last));
  return <span className="whitespace-pre-wrap break-words">{nodes}</span>;
}

export default function AssistantWidget() {
  const { lang, user, authHeaders } = useApp();
  const es = lang === "es";
  const roleKey = user?.role === "hirer" ? "hirer" : user?.role === "laborer" ? "worker" : "guest";
  const greeting = es
    ? (roleKey === "worker"
        ? "¡Hola! 👋 Soy el asistente de ChambaNow. Puedo ayudarte a completar tu perfil, configurar tu disponibilidad y encontrar trabajos cerca de ti. ¿En qué te ayudo?"
      : roleKey === "hirer"
        ? "¡Hola! 👋 Soy el asistente de ChambaNow. Puedo ayudarte a publicar un trabajo, encontrar trabajadores y gestionar postulantes. ¿En qué te ayudo?"
        : "¡Hola! 👋 Soy el asistente de ChambaNow, para trabajadores y contratantes. ¿Buscas contratar o buscas trabajo? Puedo ayudarte a empezar o responder tus preguntas.")
    : (roleKey === "worker"
        ? "Hi! 👋 I'm the ChambaNow assistant. I can help you finish your profile, set your availability, and find jobs near you. What can I help with?"
      : roleKey === "hirer"
        ? "Hi! 👋 I'm the ChambaNow assistant. I can help you post a job, find workers near you, and manage applicants. What can I help with?"
        : "Hi! 👋 I'm the ChambaNow assistant — for workers and hirers. Are you here to hire or to find work? I can help you get started or answer any questions.");
  const T = {
    open: es ? "Asistente" : "Assistant",
    title: es ? "Asistente ChambaNow" : "ChambaNow Assistant",
    subtitle: es ? "Aquí para ayudarte" : "Here to help",
    greeting,
    placeholder: es ? "Escribe tu mensaje…" : "Type your message…",
    thinking: es ? "Escribiendo…" : "Typing…",
    talkPerson: es ? "Hablar con una persona" : "Talk to a person",
    handoffTitle: es ? "Hablar con una persona" : "Talk to a person",
    handoffBody: es
      ? "Deja tu correo y le enviaremos tu conversación al equipo de ChambaNow. Te responderán pronto."
      : "Leave your email and we'll send your conversation to the ChambaNow team. They'll get back to you soon.",
    emailLabel: es ? "Tu correo" : "Your email",
    nameLabel: es ? "Tu nombre (opcional)" : "Your name (optional)",
    noteLabel: es ? "¿Cómo podemos ayudar? (opcional)" : "How can we help? (optional)",
    sendReq: es ? "Enviar al equipo" : "Send to the team",
    back: es ? "Volver" : "Back",
    sentTitle: es ? "¡Mensaje enviado!" : "Message sent!",
    sentBody: es ? "El equipo de ChambaNow te contactará pronto." : "The ChambaNow team will reach out soon.",
    errGeneric: es ? "Algo salió mal. Intenta de nuevo." : "Something went wrong. Please try again.",
    emailInvalid: es ? "Ingresa un correo válido." : "Enter a valid email.",
    teamReplied: es ? "Respuesta del equipo de ChambaNow:" : "Reply from the ChambaNow team:",
  };

  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | handoff | sent
  const [messages, setMessages] = useState([{ role: "assistant", content: T.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [hoName, setHoName] = useState("");
  const [hoEmail, setHoEmail] = useState("");
  const [hoNote, setHoNote] = useState("");
  const [hoBusy, setHoBusy] = useState(false);
  const [humanReplies, setHumanReplies] = useState([]); // unseen admin replies
  const scrollRef = useRef(null);

  // Poll for human (admin) replies to a past "Talk to a person" request.
  useEffect(() => {
    if (!user) { setHumanReplies([]); return; }
    let stop = false;
    const fetchReplies = async () => {
      try {
        const r = await axios.get(`${API}/assistant/replies`, { headers: authHeaders() });
        if (!stop) setHumanReplies(r.data.replies || []);
      } catch { /* ignore */ }
    };
    fetchReplies();
    const id = setInterval(fetchReplies, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [user, authHeaders]);

  // When the user opens the widget and there are unseen human replies, inject them
  // into the chat as ChambaNow-team messages and mark them seen (clears the badge).
  useEffect(() => {
    if (!open || humanReplies.length === 0) return;
    setMessages((prev) => [
      ...prev,
      ...humanReplies.map((hr) => ({ role: "assistant", content: `**${T.teamReplied}**\n${hr.body}` })),
    ]);
    setView("chat");
    axios.post(`${API}/assistant/replies/seen`, {}, { headers: authHeaders() }).catch(() => {});
    setHumanReplies([]);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy, open, view]);

  // Refresh the greeting to match the audience when auth/role changes — but only
  // if the conversation hasn't started yet (don't wipe an active chat).
  useEffect(() => {
    setMessages(prev => (prev.length <= 1 ? [{ role: "assistant", content: greeting }] : prev));
  }, [roleKey, es]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assistant/chat`, { messages: next.map(m => ({ role: m.role, content: m.content })), lang, role: user?.role || "" });
      setMessages([...next, { role: "assistant", content: r.data.reply || T.errGeneric }]);
    } catch {
      setMessages([...next, { role: "assistant", content: T.errGeneric }]);
    } finally {
      setBusy(false);
    }
  };

  const sendHandoff = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(hoEmail.trim())) { setView("handoff"); alert(T.emailInvalid); return; }
    setHoBusy(true);
    try {
      await axios.post(`${API}/assistant/handoff`, {
        messages: messages.map(m => ({ role: m.role, content: m.content })),
        email: hoEmail.trim(), name: hoName.trim(), note: hoNote.trim(),
      }, { headers: authHeaders() });
      setView("sent");
    } catch {
      alert(T.errGeneric);
    } finally {
      setHoBusy(false);
    }
  };

  return (
    <>
      {/* Floating toggle bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-toggle"
          aria-label={T.open}
          className={`fixed ${user?.role === "laborer" ? "bottom-24" : "bottom-5"} right-5 z-[1000] flex items-center gap-2 rounded-full bg-[#FF7A1A] hover:bg-[#ff8b35] text-white shadow-2xl shadow-[#FF7A1A]/30 pl-4 pr-5 py-3 transition-all hover:scale-105`}
        >
          <ChatCircleDots size={22} weight="fill" />
          <span className="font-bold text-sm uppercase tracking-wide hidden sm:inline">{T.open}</span>
          {humanReplies.length > 0 && (
            <span
              data-testid="assistant-reply-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-[#0d0d0f]"
            >
              {humanReplies.length}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-5 right-5 z-[1000] w-[calc(100vw-2.5rem)] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col rounded-2xl border border-white/15 bg-[#0d0d0f] shadow-2xl overflow-hidden"
        >
          {/* header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#FF7A1A]/20 to-transparent">
            <div className="w-9 h-9 rounded-full bg-[#FF7A1A] flex items-center justify-center flex-shrink-0">
              <ChatCircleDots size={18} weight="fill" color="#fff" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm uppercase tracking-tight text-white leading-tight">{T.title}</div>
              <div className="text-[11px] text-emerald-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {T.subtitle}</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" aria-label="Close" className="text-zinc-400 hover:text-white p-1">
              <X size={20} weight="bold" />
            </button>
          </div>

          {view === "chat" && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
                {messages.map((m, i) => (
                  <div key={i} data-testid={`assistant-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#FF7A1A] text-white rounded-br-sm" : "bg-zinc-800 text-zinc-200 rounded-bl-sm"}`}>
                      <RichText text={m.content} onNavigate={() => setOpen(false)} />
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 text-zinc-400 rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm italic">{T.thinking}</div>
                  </div>
                )}
              </div>

              <div className="border-t border-white/10 px-3 py-2">
                <button
                  onClick={() => setView("handoff")}
                  data-testid="assistant-handoff-btn"
                  className="w-full mb-2 flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-400 hover:text-[#FF7A1A] transition-colors py-1"
                >
                  <Headset size={14} weight="fill" /> {T.talkPerson}
                </button>
                <div className="flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={1}
                    placeholder={T.placeholder}
                    data-testid="assistant-input"
                    className="flex-1 resize-none rounded-xl bg-zinc-900 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF7A1A]/60 max-h-24"
                  />
                  <button onClick={send} disabled={busy || !input.trim()} data-testid="assistant-send" aria-label="Send"
                    className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#FF7A1A] hover:bg-[#ff8b35] disabled:opacity-40 text-white flex items-center justify-center transition-colors">
                    <PaperPlaneTilt size={18} weight="fill" />
                  </button>
                </div>
              </div>
            </>
          )}

          {view === "handoff" && (
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <button onClick={() => setView("chat")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white mb-3"><ArrowLeft size={14} weight="bold" /> {T.back}</button>
              <h3 className="font-display text-lg uppercase tracking-tight text-white">{T.handoffTitle}</h3>
              <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{T.handoffBody}</p>
              <div className="mt-4 space-y-3">
                <input value={hoName} onChange={(e) => setHoName(e.target.value)} placeholder={T.nameLabel} data-testid="assistant-handoff-name"
                  className="w-full rounded-xl bg-zinc-900 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF7A1A]/60" />
                <input value={hoEmail} onChange={(e) => setHoEmail(e.target.value)} type="email" placeholder={T.emailLabel} data-testid="assistant-handoff-email"
                  className="w-full rounded-xl bg-zinc-900 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF7A1A]/60" />
                <textarea value={hoNote} onChange={(e) => setHoNote(e.target.value)} rows={3} placeholder={T.noteLabel} data-testid="assistant-handoff-note"
                  className="w-full resize-none rounded-xl bg-zinc-900 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF7A1A]/60" />
                <button onClick={sendHandoff} disabled={hoBusy} data-testid="assistant-handoff-send"
                  className="w-full btn-primary disabled:opacity-50">{hoBusy ? "…" : T.sendReq}</button>
              </div>
            </div>
          )}

          {view === "sent" && (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="assistant-handoff-sent">
              <CheckCircle size={48} weight="fill" className="text-emerald-400" />
              <h3 className="mt-4 font-display text-xl uppercase tracking-tight text-white">{T.sentTitle}</h3>
              <p className="mt-2 text-sm text-zinc-400">{T.sentBody}</p>
              <button onClick={() => { setView("chat"); }} className="mt-6 btn-secondary">{T.back}</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
